import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { MaisAcoes } from '@/componentes/MaisAcoes';
import { RodapeDeContagem, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarCompetencia, formatarDecimal, formatarReais, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { ehFichaAlterada } from '@/lib/versao-da-ficha';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDoOrcamento, type Cenario, type Cenarios, type Simulacao, type TotaisDoCenario, type VariaveisDoCenario, type VersaoNaLista } from './api';
import {
  CENARIO_NOVO,
  corpoDoCenario,
  detalheDaHipotese,
  detalheDoCenario,
  faltaNoCenario,
  reaisComSinal,
  valoresDoCenario,
  variaveisEscritas,
  type ValoresDoCenario,
} from './orcamento';
import type { AcaoDoCabecalho } from './PaginaOrcamento';
import { PremissasDoNumero } from './PrevistoRealizado';

interface LinhaDaComparacao extends TotaisDoCenario {
  readonly id: string;
  readonly nome: string;
  readonly detalhe: string;
  readonly cenario: (Cenario & { readonly completo: boolean }) | null;
}

/**
 * FERRAMENTA 3 DO MÓDULO 19 — os cenários operacionais. A frase de que cenário
 * não é previsão vem do servidor e fica no topo, junto do número; com um
 * cenário só, o aviso diz o risco. A tabela compara o orçado com cada
 * cenário; escolher um abre a simulação abaixo dela: a demanda por tipo e
 * contrato (o que o módulo 20 lê), a sensibilidade de ±10% e as premissas.
 */
export function CenariosDoOrcamento({
  versao,
  carregandoVersoes,
  acao,
  aoFecharAcao,
}: {
  readonly versao: VersaoNaLista | null;
  readonly carregandoVersoes: boolean;
  readonly acao: AcaoDoCabecalho;
  readonly aoFecharAcao: () => void;
}) {
  const sessao = useSessao();
  const grava = (sessao.data?.permissoes ?? []).includes('orcamento.gravar');
  const consulta = useQuery({
    queryKey: ['orcamento', 'cenarios', versao?.id ?? null],
    queryFn: () => apiDoOrcamento.cenarios(versao?.id ?? ''),
    enabled: versao !== null,
    meta: { oQueLia: 'os cenários' },
  });
  const [escolhido, setEscolhido] = useState<string | null>(null);
  const [editando, setEditando] = useState<Cenario | null>(null);
  const [removendo, setRemovendo] = useState<Cenario | null>(null);
  const d = consulta.data;

  if (!carregandoVersoes && !versao) {
    return <EstadoVazio titulo="Nenhuma versão do orçamento" descricao="O cenário se monta sobre uma versão do orçamento. Crie a versão na aba Orçamento por centro." />;
  }
  if (consulta.isError && !d) return <ErroDaLista oQue="Os cenários" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;

  const linhas: LinhaDaComparacao[] = d
    ? [
      { id: 'orcado', nome: 'Orçado', detalhe: `Versão ${String(d.versao.versao)} de ${String(d.versao.exercicio)}`, cenario: null, ...d.orcado },
      ...d.cenarios.map((c) => ({ id: c.id, nome: c.nome, detalhe: variaveisEscritas(c, d.base.precoDieselDaPremissa), cenario: c, ...c.total })),
    ]
    : [];
  const aberto = d?.cenarios.find((c) => c.id === escolhido) ?? d?.cenarios[0] ?? null;

  return (
    <>
      <FraseDoCenario frase={d?.frase} aviso={d?.avisoDeCenarioUnico ?? null} />

      <Tabela
        rotulo="O orçado e cada cenário, no exercício"
        colunas={colunasDaComparacao(d?.orcado.resultado ?? 0)}
        linhas={linhas}
        chaveDaLinha={(l) => l.id}
        carregando={consulta.isPending || (versao !== null && !d)}
        alturaDupla
        selecionada={aberto?.id ?? null}
        aoSelecionar={(l) => { if (l.cenario) setEscolhido(l.cenario.id); }}
        {...(grava
          ? {
              menu: (l: LinhaDaComparacao) =>
                l.cenario ? (
                  <MaisAcoes
                    forma="icone"
                    acoes={[
                      { rotulo: 'Editar cenário', aoEscolher: () => { if (l.cenario) setEditando(l.cenario); } },
                      { rotulo: 'Remover cenário', aoEscolher: () => { if (l.cenario) setRemovendo(l.cenario); } },
                    ]}
                  />
                ) : null,
            }
          : {})}
        vazio={<EstadoVazio titulo="Nenhum cenário" descricao="Monte o primeiro cenário e um pessimista para comparar." />}
        rodape={<RodapeDeContagem total={linhas.length} />}
      />
      {d && d.cenarios.length === 0 ? (
        <EstadoVazio
          titulo="Nenhum cenário nesta versão"
          descricao={grava ? 'Crie um cenário: a utilização, o preço do diesel, o valor-hora e a manutenção, sobre a demanda do histórico.' : 'Ninguém montou cenário nesta versão. Peça ao perfil Controladoria.'}
        />
      ) : null}

      {d && aberto ? <SimulacaoDoCenario key={aberto.id} cenario={aberto} base={d} grava={grava} /> : null}

      {d && grava ? (
        <JanelaDoCenario
          key={editando?.id ?? (acao === 'cenario' ? 'novo' : 'fechada')}
          versaoId={d.versao.id}
          cenario={editando}
          aberta={editando !== null || acao === 'cenario'}
          precoDaPremissa={d.base.precoDieselDaPremissa}
          aoFechar={() => { setEditando(null); aoFecharAcao(); }}
          aoCriar={setEscolhido}
        />
      ) : null}
      {removendo ? <JanelaDeRemocao cenario={removendo} aoFechar={() => { setRemovendo(null); }} /> : null}
    </>
  );
}

/** "Cenário não é previsão" — do servidor, no topo, junto do número. */
export function FraseDoCenario({ frase, aviso }: { readonly frase: string | undefined; readonly aviso: string | null }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="bloco-rebaixado flex items-start gap-3 rounded-controle p-4">
        <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-informacao" />
        {frase ? (
          <p className="txt-corpo text-pretty text-tinta">{frase}</p>
        ) : (
          <Carregando oQue="Carregando a frase do cenário">
            <Barra className="my-1 h-3 w-96 max-w-full" />
          </Carregando>
        )}
      </div>
      {aviso ? (
        <p className="txt-apoio flex items-start gap-2 text-pretty text-atencao-texto">
          <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          {aviso}
        </p>
      ) : null}
    </div>
  );
}

function colunasDaComparacao(resultadoOrcado: number): readonly Coluna<LinhaDaComparacao>[] {
  const reais = (v: number, alerta = false) => <span className={`numero whitespace-nowrap ${alerta && v < 0 ? 'text-parada-texto' : 'text-tinta'}`}>{reaisComSinal(v)}</span>;
  return [
    {
      chave: 'nome',
      rotulo: 'Cenário',
      prioridade: 'chave',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className={`txt-corpo ${l.cenario ? 'text-tinta' : 'font-semibold text-tinta'}`}>{l.nome}</span>
          <TextoDaCelula texto={l.detalhe} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    { chave: 'receita', rotulo: 'Receita', alinhamento: 'numero', celula: (l) => reais(l.receita) },
    { chave: 'combustivel', rotulo: 'Combustível', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => reais(l.combustivel) },
    { chave: 'manutencao', rotulo: 'Manutenção', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => reais(l.manutencao) },
    { chave: 'fixos', rotulo: 'Fixos', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => reais(l.fixos) },
    {
      chave: 'resultado',
      rotulo: 'Resultado',
      alinhamento: 'numero',
      celula: (l) => (
        <span className="flex flex-col items-end">
          {reais(l.resultado, true)}
          {l.cenario && !l.cenario.completo ? <span className="txt-apoio whitespace-nowrap text-atencao-texto">parcial</span> : null}
        </span>
      ),
    },
    {
      chave: 'diferenca',
      rotulo: 'Contra o orçado',
      alinhamento: 'numero',
      celula: (l) =>
        l.cenario && !l.cenario.completo ? (
          /* O cenario parcial (falta o custo por hora de algum tipo) nao se
             compara com o orcado inteiro: a diferenca seria o custo que faltou. */
          <span className="txt-apoio text-atencao-texto">não comparável</span>
        ) : l.cenario ? (
          <span className={`numero whitespace-nowrap ${l.resultado - resultadoOrcado < 0 ? 'text-parada-texto' : 'text-operando-texto'}`}>{reaisComSinal(l.resultado - resultadoOrcado)}</span>
        ) : (
          <span className="txt-apoio text-tinta-apagada">base</span>
        ),
    },
  ];
}

interface LinhaDaDemanda {
  readonly id: string;
  readonly tipo: string;
  readonly contratoId: string | null;
  readonly contratoCodigo: string | null;
  readonly origem: 'historico' | 'hipotese';
  readonly horasMes: number;
  readonly receitaMes: number | null;
  readonly combustivelMes: number | null;
  readonly manutencaoMes: number | null;
}

function SimulacaoDoCenario({ cenario, base, grava }: { readonly cenario: Cenario; readonly base: Cenarios; readonly grava: boolean }) {
  const simulacao = useQuery({ queryKey: ['orcamento', 'simulacao', cenario.id, cenario.atualizadoEm], queryFn: () => apiDoOrcamento.simulacao(cenario.id), meta: { oQueLia: 'a simulação do cenário' } });
  const [demanda, setDemanda] = useState<LinhaDaDemanda | 'nova' | null>(null);
  const s: Simulacao | undefined = simulacao.data;
  const linhas: LinhaDaDemanda[] = (s?.resultado.porTipo ?? []).map((t) => ({ id: `${t.tipo}|${t.contratoId ?? ''}`, ...t }));
  const reais = (v: number | null) => (v === null ? <span className="txt-apoio text-atencao-texto">sem histórico</span> : <span className="numero whitespace-nowrap text-tinta">{formatarReais(v)}</span>);
  const colunas: readonly Coluna<LinhaDaDemanda>[] = [
    {
      chave: 'tipo',
      rotulo: 'Tipo e contrato',
      prioridade: 'chave',
      celula: (l) => (
        <span className="flex min-w-0 flex-col">
          <span className="txt-corpo text-tinta">{l.tipo}</span>
          <TextoDaCelula texto={`${l.contratoCodigo ?? 'Sem contrato'} · ${l.origem === 'hipotese' ? 'hipótese do cenário' : 'média do histórico'}`} classe="txt-apoio text-tinta-apagada" linhas={1} />
        </span>
      ),
    },
    { chave: 'horas', rotulo: 'Horas por mês', alinhamento: 'numero', celula: (l) => <span className="numero whitespace-nowrap text-tinta">{`${formatarDecimal(l.horasMes, 1)} h`}</span> },
    { chave: 'receita', rotulo: 'Receita por mês', alinhamento: 'numero', celula: (l) => reais(l.receitaMes) },
    { chave: 'combustivel', rotulo: 'Combustível', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => reais(l.combustivelMes) },
    { chave: 'manutencao', rotulo: 'Manutenção', alinhamento: 'numero', prioridade: 'secundaria', celula: (l) => reais(l.manutencaoMes) },
  ];
  return (
    <section className="flex flex-col gap-4" aria-label={`Simulação do cenário ${cenario.nome}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 className="txt-secao text-tinta">{`Cenário ${cenario.nome}`}</h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">{variaveisEscritas(cenario, base.base.precoDieselDaPremissa)}{cenario.observacao ? ` · ${cenario.observacao}` : ''}</p>
        </div>
        {grava ? (
          <Botao nivel="secundario" onClick={() => { setDemanda('nova'); }}>
            Mudar demanda
          </Botao>
        ) : null}
      </div>

      {simulacao.isError && !s ? (
        <ErroDaLista oQue="A simulação do cenário" erro={simulacao.error} aoTentarDeNovo={() => void simulacao.refetch()} />
      ) : (
        <>
          <Tabela
            rotulo="Demanda por tipo de máquina e contrato"
            colunas={colunas}
            linhas={linhas}
            chaveDaLinha={(l) => l.id}
            carregando={simulacao.isPending}
            erro={simulacao.isError ? <ErroDaLista oQue="A simulação do cenário" erro={simulacao.error} aoTentarDeNovo={() => void simulacao.refetch()} /> : null}
            alturaDupla
            {...(grava ? { aoSelecionar: (l: LinhaDaDemanda) => { setDemanda(l); } } : {})}
            vazio={
              <EstadoVazio
                titulo="Nenhuma demanda"
                descricao="Sem medição aprovada nos meses fechados e sem hipótese: o cenário não tem horas. Informe a demanda de um tipo de máquina num contrato."
              />
            }
            rodape={<RodapeDeContagem total={linhas.length} />}
          />
          <h2 className="txt-secao text-tinta">Quanto o resultado muda com 10% em cada variável?</h2>
          <Tabela
            rotulo="Sensibilidade do resultado do exercício"
            colunas={[
              { chave: 'variavel', rotulo: 'Variável', prioridade: 'chave', celula: (x) => <span className="txt-corpo text-tinta">{x.rotulo}</span> },
              { chave: 'menos', rotulo: 'A 90%', alinhamento: 'numero', celula: (x) => <span className={`numero whitespace-nowrap ${x.deltaMenos10 < 0 ? 'text-parada-texto' : 'text-operando-texto'}`}>{reaisComSinal(x.deltaMenos10)}</span> },
              { chave: 'mais', rotulo: 'A 110%', alinhamento: 'numero', celula: (x) => <span className={`numero whitespace-nowrap ${x.deltaMais10 < 0 ? 'text-parada-texto' : 'text-operando-texto'}`}>{reaisComSinal(x.deltaMais10)}</span> },
              { chave: 'resultado', rotulo: 'Resultado a 110%', alinhamento: 'numero', prioridade: 'secundaria', celula: (x) => <span className="numero whitespace-nowrap text-tinta">{reaisComSinal(x.mais10)}</span> },
            ]}
            linhas={s?.sensibilidade ?? []}
            chaveDaLinha={(x) => x.variavel}
            carregando={simulacao.isPending}
            erro={simulacao.isError ? <ErroDaLista oQue="A simulação do cenário" erro={simulacao.error} aoTentarDeNovo={() => void simulacao.refetch()} /> : null}
            vazio={<EstadoVazio titulo="Sem sensibilidade" descricao="Sem demanda, nenhuma variável muda o resultado. Informe a demanda do cenário." />}
            rodape={<p className="txt-apoio px-4 py-3 text-tinta-secundaria">A demanda e a utilização multiplicam as mesmas horas: 10% numa é 10% na outra.</p>}
          />
          {s ? (
            <p className="txt-apoio text-tinta-secundaria">
              {`Por mês: ${s.resultado.mensal.map((m) => `${formatarCompetencia(m.competencia)} ${reaisComSinal(m.resultado)}`).join(' · ')}`}
            </p>
          ) : null}
          <PremissasDoNumero titulo="Como o cenário é calculado" premissas={s?.premissas} frase={s?.frase} />
        </>
      )}
      {grava ? (
        <JanelaDaDemanda
          key={demanda === null ? 'fechada' : demanda === 'nova' ? 'nova' : demanda.id}
          cenario={cenario}
          base={base}
          linha={demanda}
          aoFechar={() => { setDemanda(null); }}
        />
      ) : null}
    </section>
  );
}

function JanelaDoCenario({
  versaoId,
  cenario,
  aberta,
  precoDaPremissa,
  aoFechar,
  aoCriar,
}: {
  readonly versaoId: string;
  readonly cenario: Cenario | null;
  readonly aberta: boolean;
  readonly precoDaPremissa: number | null;
  readonly aoFechar: () => void;
  readonly aoCriar: (id: string) => void;
}) {
  const cliente = useQueryClient();
  const [v, setV] = useState<ValoresDoCenario>(() => (cenario ? valoresDoCenario(cenario) : CENARIO_NOVO));
  /* A versao de quando o formulario abriu (robos de 20/10/2026): o servidor
     recusa o formulario velho em vez de gravar por cima de outra pessoa. */
  const [versao] = useState<string | null>(() => cenario?.versao ?? null);
  const [tentou, setTentou] = useState(false);
  const falta = faltaNoCenario(v);
  const recarregar = () => cliente.invalidateQueries({ queryKey: ['orcamento'] });
  const criar = useMutation({
    mutationFn: () => apiDoOrcamento.criarCenario(versaoId, corpoDoCenario(v)),
    meta: { retorno: retornoDe<{ id: string } & VariaveisDoCenario>((r) => ({ titulo: 'Cenário criado', ...detalheDoCenario(r, precoDaPremissa) })) },
    onSuccess: async (r) => { aoFechar(); await recarregar(); aoCriar(r.id); },
  });
  const alterar = useMutation({
    mutationFn: () => apiDoOrcamento.alterarCenario(cenario?.id ?? '', corpoDoCenario(v), versao),
    meta: { retorno: retornoDe<VariaveisDoCenario>((r) => ({ titulo: 'Cenário alterado', ...detalheDoCenario(r, precoDaPremissa) })) },
    onSuccess: async () => { aoFechar(); await recarregar(); },
    /* Recusada por versao velha: os cenarios releem, e reabrir mostra o de agora. */
    onError: (erro) => { if (ehFichaAlterada(erro)) void recarregar(); },
  });
  const recusa = criar.error ?? alterar.error;
  const erro = (campo: keyof ValoresDoCenario) => (tentou ? (falta[campo] ?? null) : null);
  return (
    <Janela
      titulo={cenario ? `Editar o cenário ${cenario.nome}` : 'Criar cenário'}
      descricao="As variáveis são relativas ao histórico (100% = como foi). Cenário não é previsão."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={criar.isPending || alterar.isPending}
            onClick={() => {
              setTentou(true);
              if (Object.keys(falta).length > 0) return;
              if (cenario) alterar.mutate();
              else criar.mutate();
            }}
          >
            {criar.isPending || alterar.isPending ? 'Gravando…' : cenario ? 'Salvar cenário' : 'Criar cenário'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {recusa ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(recusa).erro}. ${explicarFalha(recusa).comoResolver}`}</p> : null}
        <CampoTexto rotulo="Nome" valor={v.nome} aoMudar={(nome) => { setV({ ...v, nome }); }} placeholder="Pessimista" validar={() => erro('nome')} maxLength={60} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <CampoTexto rotulo="Utilização (% do histórico)" valor={v.utilizacao} aoMudar={(utilizacao) => { setV({ ...v, utilizacao }); }} validar={() => erro('utilizacao')} maxLength={6} />
          <CampoTexto
            rotulo="Preço do diesel (R$/L)"
            opcional
            valor={v.precoDiesel}
            aoMudar={(precoDiesel) => { setV({ ...v, precoDiesel }); }}
            placeholder={precoDaPremissa === null ? '6,29' : String(precoDaPremissa).replace('.', ',')}
            ajuda={precoDaPremissa === null ? 'A versão não tem o preço do diesel: o combustível não varia.' : `Em branco, o da premissa: ${formatarReais(precoDaPremissa)}/L.`}
            validar={() => erro('precoDiesel')}
            maxLength={8}
          />
          <CampoTexto rotulo="Valor-hora (% do histórico)" valor={v.valorHora} aoMudar={(valorHora) => { setV({ ...v, valorHora }); }} validar={() => erro('valorHora')} maxLength={6} />
          <CampoTexto rotulo="Manutenção por hora (% do histórico)" valor={v.manutencao} aoMudar={(manutencao) => { setV({ ...v, manutencao }); }} validar={() => erro('manutencao')} maxLength={6} />
        </div>
        <CampoTexto rotulo="Observação" opcional valor={v.observacao} aoMudar={(observacao) => { setV({ ...v, observacao }); }} placeholder="Perda do contrato da obra norte em março" maxLength={400} />
      </div>
    </Janela>
  );
}

function JanelaDaDemanda({ cenario, base, linha, aoFechar }: { readonly cenario: Cenario; readonly base: Cenarios; readonly linha: LinhaDaDemanda | 'nova' | null; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const opcoes = useQuery({ queryKey: ['orcamento', 'opcoes'], queryFn: apiDoOrcamento.opcoes, staleTime: 60_000 });
  const existente = linha !== null && linha !== 'nova' ? linha : null;
  const [tipo, setTipo] = useState(existente?.tipo ?? '');
  const [contrato, setContrato] = useState(existente?.contratoId ?? '');
  const [horas, setHoras] = useState(existente && existente.origem === 'hipotese' ? String(existente.horasMes).replace('.', ',') : '');
  const [fonte, setFonte] = useState(cenario.hipoteses.find((h) => h.tipo === existente?.tipo && h.contratoId === existente.contratoId)?.fonte ?? '');
  const [tentou, setTentou] = useState(false);
  const tipos = [...new Set([...base.base.demanda.map((x) => x.tipo), ...(opcoes.data?.tipos ?? [])])].sort();
  const numero = horas.trim() === '' ? null : interpretarNumero(horas);
  const faltaTipo = tipo === '' ? 'Escolha o tipo de máquina.' : null;
  const faltaHoras = horas.trim() !== '' && (numero === null || numero < 0) ? 'Escreva as horas por mês, por exemplo 320, ou deixe em branco para voltar à média do histórico.' : null;
  const faltaFonte = fonte.trim().length < 5 ? 'Escreva de onde vem a demanda, por exemplo "proposta comercial da obra nova".' : null;
  const daBase = base.base.demanda.find((x) => x.tipo === tipo && (x.contratoId ?? '') === contrato);
  const gravar = useMutation({
    mutationFn: () => apiDoOrcamento.gravarHipotese(cenario.id, { tipo, contratoId: contrato || null, horasMes: numero, fonte }),
    meta: { retorno: retornoDe<{ cenario: string; tipo: string; contrato: string | null; horasMes: number | null; antes: number | null }>((r) => ({ titulo: r.horasMes === null ? 'Demanda devolvida ao histórico' : 'Demanda do cenário gravada', ...detalheDaHipotese(r) })) },
    onSuccess: async () => { aoFechar(); await cliente.invalidateQueries({ queryKey: ['orcamento'] }); },
  });
  return (
    <Janela
      titulo={`Demanda no cenário ${cenario.nome}`}
      descricao="As horas por mês de um tipo de máquina num contrato. Em branco, vale a média do histórico."
      aberta={linha !== null}
      aoFechar={aoFechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={gravar.isPending} onClick={() => { setTentou(true); if (!faltaTipo && !faltaHoras && !faltaFonte) gravar.mutate(); }}>
            {gravar.isPending ? 'Gravando…' : numero === null ? 'Voltar ao histórico' : 'Gravar demanda'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {gravar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(gravar.error).erro}. ${explicarFalha(gravar.error).comoResolver}`}</p> : null}
        <CampoSelecao rotulo="Tipo de máquina" valor={tipo} aoMudar={setTipo} rotuloVazio="Escolha o tipo" opcoes={tipos.map((t) => ({ valor: t, rotulo: t }))} />
        {tentou && faltaTipo ? <p className="txt-apoio text-parada-texto">{faltaTipo}</p> : null}
        <CampoSelecao
          rotulo="Contrato"
          opcional
          valor={contrato}
          aoMudar={setContrato}
          rotuloVazio="Sem contrato"
          opcoes={(opcoes.data?.contratos ?? []).map((c) => ({ valor: c.id, rotulo: `${c.codigo} · ${c.descricao}` }))}
        />
        <CampoTexto
          rotulo="Horas por mês"
          opcional
          valor={horas}
          aoMudar={setHoras}
          ajuda={daBase ? `Média do histórico: ${formatarDecimal(daBase.horasMes, 1)} h por mês.` : 'Sem histórico para este tipo e contrato.'}
          validar={() => (tentou ? faltaHoras : null)}
          maxLength={10}
        />
        <CampoTexto rotulo="Fonte" valor={fonte} aoMudar={setFonte} placeholder="Proposta comercial da obra nova" validar={() => (tentou ? faltaFonte : null)} maxLength={200} />
      </div>
    </Janela>
  );
}

function JanelaDeRemocao({ cenario, aoFechar }: { readonly cenario: Cenario; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const remover = useMutation({
    mutationFn: () => apiDoOrcamento.removerCenario(cenario.id),
    meta: { retorno: retornoDe<{ nome: string }>((r) => ({ titulo: 'Cenário removido', alvo: { nome: r.nome }, fatos: [`${String(cenario.hipoteses.length)} ${cenario.hipoteses.length === 1 ? 'hipótese' : 'hipóteses'} de demanda`], efeito: 'O orçado e os outros cenários não mudam' })) },
    onSuccess: async () => { aoFechar(); await cliente.invalidateQueries({ queryKey: ['orcamento'] }); },
  });
  return (
    <Janela
      titulo={`Remover o cenário ${cenario.nome}?`}
      descricao="As hipóteses de demanda dele saem juntas. O orçado não muda, e a trilha guarda quem removeu."
      aberta
      aoFechar={aoFechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Manter cenário</Botao>
          <Botao nivel="destrutivo" disabled={remover.isPending} onClick={() => { remover.mutate(); }}>
            {remover.isPending ? 'Removendo…' : 'Remover cenário'}
          </Botao>
        </>
      }
    >
      {remover.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(remover.error).erro}. ${explicarFalha(remover.error).comoResolver}`}</p> : <p className="txt-apoio text-tinta-secundaria">{variaveisEscritas(cenario, null)}</p>}
    </Janela>
  );
}
